import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, FileText, Gift, ExternalLink, AlertTriangle } from "lucide-react";

const TABS = ["pending", "approved", "rejected", "all"];

export default function AdminVerifications() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "pending";
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);   // full row for the previewer panel
  const [modal, setModal] = useState(null);         // { action, note?, days? } for confirm dialogs
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    const s = status === "all" ? "" : `status=${status}`;
    api.get(`/admin/verifications?${s}`).then(({ data }) => {
      setItems(data.items || []);
      setSelected((prev) => (data.items || []).find((v) => v.verification_id === prev?.verification_id) || null);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const resolve = async () => {
    if (!selected || !modal) return;
    if (modal.action === "reject" && !modal.note?.trim()) {
      return toast.error("Please add a note to explain rejection");
    }
    setSubmitting(true);
    try {
      if (modal.action === "grant") {
        await api.post("/admin/grants", {
          user_email: selected.user_email,
          kind: "verified",
          days: Number(modal.days || 365),
          reason: modal.note || "Comp granted by admin from Verifications queue",
        });
        toast.success(`Verified badge granted to ${selected.user_email}`);
      } else {
        await api.post(`/admin/verifications/${selected.verification_id}/${modal.action}`, { note: modal.note });
        toast.success(modal.action === "approve" ? "Approved — user notified to pay £49" : "Rejected");
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSubmitting(false); }
  };

  const isPdf = (path) => (path || "").toLowerCase().endsWith(".pdf");

  return (
    <div>
      <div className="overline text-safety">Verifications</div>
      <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">Trust badge queue</h1>
      <p className="mt-1 text-sm text-muted-foreground">Review proof of insurance inline. Approve, reject, or comp a free badge.</p>

      <div className="mt-6 flex border border-border w-fit">
        {TABS.map((t) => (
          <button key={t} onClick={() => setParams({ status: t })} data-testid={`admin-verifications-tab-${t}`}
            className={`px-5 h-10 text-xs overline transition-colors duration-200 ${status === t ? "bg-forest text-white" : "bg-white hover:bg-sage"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr),minmax(0,1.2fr)] gap-6">
        {/* List */}
        <div className="border border-border bg-white h-fit">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Nothing in &quot;{status}&quot;</div>
          ) : items.map((v, i) => {
            const active = v.verification_id === selected?.verification_id;
            return (
              <button key={v.verification_id} onClick={() => setSelected(v)}
                data-testid={`admin-verification-row-${v.verification_id}`}
                className={`w-full text-left p-4 grid grid-cols-1 gap-1 transition-colors ${i > 0 ? "border-t border-border" : ""} ${active ? "bg-sage-light" : "hover:bg-sage"}`}>
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-none overline text-[9px] ${v.status === "approved" || v.status === "active" ? "bg-forest text-white" : v.status === "rejected" ? "bg-destructive text-white" : "bg-safety text-white"}`}>{v.status}</Badge>
                  <span className="overline text-[10px] text-muted-foreground">{new Date(v.submitted_at).toLocaleDateString("en-GB")}</span>
                </div>
                <div className="font-display text-base tracking-tight">{v.company_name || v.user_name}</div>
                <div className="text-xs text-muted-foreground">{v.user_email}</div>
              </button>
            );
          })}
        </div>

        {/* Previewer + Actions */}
        {selected ? (
          <div className="border border-border bg-white p-6 space-y-4" data-testid="verification-preview-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <Badge className={`rounded-none overline text-[9px] ${selected.status === "approved" || selected.status === "active" ? "bg-forest text-white" : selected.status === "rejected" ? "bg-destructive text-white" : "bg-safety text-white"}`}>{selected.status}</Badge>
                <div className="mt-2 font-display text-xl tracking-tight text-forest">{selected.company_name || selected.user_name}</div>
                <div className="text-xs text-muted-foreground">{selected.user_email} · {selected.user_type?.replace("_", " ")}</div>
              </div>
              <Button asChild variant="outline" size="sm" className="rounded-none">
                <a href={fileUrl(selected.doc_path)} target="_blank" rel="noreferrer" data-testid="verification-open-doc">
                  <ExternalLink className="w-4 h-4 mr-1" /> Open in new tab
                </a>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              {selected.insurance_provider && (<div><div className="overline text-muted-foreground">Insurer</div><div className="mt-0.5 font-medium">{selected.insurance_provider}</div></div>)}
              {selected.policy_number && (<div><div className="overline text-muted-foreground">Policy</div><div className="mt-0.5 font-mono">{selected.policy_number}</div></div>)}
              {selected.companies_house_number && (<div><div className="overline text-muted-foreground">Companies House</div><div className="mt-0.5 font-mono">{selected.companies_house_number}</div></div>)}
            </div>

            {selected.admin_note && (
              <div className="text-xs bg-safety/10 border-l-2 border-safety px-3 py-2 text-forest">
                <div className="overline text-safety">Previous note</div>{selected.admin_note}
              </div>
            )}

            <div className="border border-border bg-sage-light overflow-hidden relative" style={{ minHeight: 420 }} data-testid="verification-doc-preview">
              {isPdf(selected.doc_path) ? (
                <PdfPreview src={fileUrl(selected.doc_path)} />
              ) : (
                <ImgPreview src={fileUrl(selected.doc_path)} />
              )}
            </div>

            {(selected.status === "pending") && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                <Button onClick={() => setModal({ action: "approve", note: "" })} data-testid={`admin-verification-approve-${selected.verification_id}`} className="rounded-none bg-forest hover:bg-forest-800 text-white">
                  <Check className="w-4 h-4 mr-1" /> Approve (user pays £49)
                </Button>
                <Button onClick={() => setModal({ action: "grant", note: "", days: 365 })} data-testid={`admin-verification-grant-${selected.verification_id}`} className="rounded-none bg-safety hover:bg-safety/90 text-white">
                  <Gift className="w-4 h-4 mr-1" /> Grant free badge
                </Button>
                <Button onClick={() => setModal({ action: "reject", note: "" })} variant="outline" data-testid={`admin-verification-reject-${selected.verification_id}`} className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white">
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            <FileText className="w-6 h-6 mx-auto mb-2 opacity-40" />
            Select a submission to preview the uploaded document and take action.
          </div>
        )}
      </div>

      <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="bg-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {modal?.action === "approve" && "Approve — user will be emailed to pay £49"}
              {modal?.action === "grant" && "Grant free verified badge"}
              {modal?.action === "reject" && "Reject verification"}
            </DialogTitle>
          </DialogHeader>
          {selected && modal && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="overline text-muted-foreground">Applicant</div>
                <div className="mt-1">{selected.company_name || selected.user_name} ({selected.user_email})</div>
              </div>
              {modal.action === "grant" && (
                <div>
                  <div className="overline text-muted-foreground mb-2">Duration (days)</div>
                  <Input type="number" min="1" max="3650" value={modal.days ?? 365}
                    onChange={(e) => setModal({ ...modal, days: e.target.value })}
                    data-testid="verification-grant-days" className="rounded-none h-11 max-w-[120px]" />
                </div>
              )}
              <div>
                <div className="overline text-muted-foreground mb-2">
                  {modal.action === "reject" ? "Reason for rejection (sent to user) *" : "Note (audit log)"}
                </div>
                <Textarea rows={3} value={modal.note ?? ""} onChange={(e) => setModal({ ...modal, note: e.target.value })} data-testid="admin-verification-note" className="rounded-none" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)} className="rounded-none">Cancel</Button>
            <Button onClick={resolve} disabled={submitting} data-testid="admin-verification-submit"
              className={`rounded-none text-white ${modal?.action === "reject" ? "bg-destructive hover:opacity-90" : modal?.action === "grant" ? "bg-safety hover:bg-safety/90" : "bg-forest hover:bg-forest-800"}`}>
              {submitting ? "Working…" :
                modal?.action === "approve" ? "Confirm approval" :
                modal?.action === "grant"   ? "Grant free badge" :
                                              "Confirm rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Preview components with CSP-safe fallback ----------
function FallbackBanner({ src }) {
  return (
    <div className="p-6 flex flex-col items-center justify-center text-center gap-3 bg-white h-full min-h-[420px]" data-testid="preview-fallback">
      <AlertTriangle className="w-6 h-6 text-safety" />
      <div className="font-display text-sm text-forest">Preview blocked</div>
      <p className="text-xs text-muted-foreground max-w-xs">
        Your browser or a security header blocked the inline preview. Open the document in a new tab to review it.
      </p>
      <a href={src} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 h-10 px-4 bg-forest text-white text-xs overline hover:bg-forest-800">
        <ExternalLink className="w-4 h-4" /> Open document
      </a>
    </div>
  );
}

function PdfPreview({ src }) {
  const [failed, setFailed] = useState(false);
  const iframeRef = useRef(null);
  useEffect(() => {
    setFailed(false);
    // If iframe never loads within 6s (blocked / X-Frame-Options / CSP), degrade.
    const t = setTimeout(() => {
      const el = iframeRef.current;
      let loaded = false;
      try { loaded = !!el && !!el.contentDocument; } catch { loaded = false; }
      if (!loaded) setFailed(true);
    }, 6000);
    return () => clearTimeout(t);
  }, [src]);
  if (failed) return <FallbackBanner src={src} />;
  return (
    <iframe
      ref={iframeRef}
      title="Document"
      src={src}
      className="w-full block"
      style={{ height: 520 }}
      onLoad={() => setFailed(false)}
      onError={() => setFailed(true)}
    />
  );
}

function ImgPreview({ src }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <FallbackBanner src={src} />;
  return (
    <img
      src={src}
      alt="Document"
      className="w-full h-auto max-h-[520px] object-contain bg-white"
      onError={() => setFailed(true)}
    />
  );
}
